/**
 * components/pricing/strategy-section-groups.ts
 *
 * The five groups both pricing surfaces file their sections under.
 *
 * The strategy editor lists up to sixteen sections and the setup wizard walks
 * up to fourteen steps over the same ground, and a list that long is read down
 * rather than glanced at. The groups cut it into five runs an operator can
 * name: what the property is and what Optivo aims for, the prices themselves,
 * the rules a night is sold under, the calendar, and the channel plumbing.
 *
 * A group is a contiguous run of the canonical order, never a reshuffle of it
 * (`CANONICAL_SECTION_ORDER`): the order is a decision of its own — Property
 * Identity first, Day Adjustments ahead of the price band — and grouping must
 * not undo it. Each surface declares its groups in its own table
 * (`EDITOR_SECTIONS`, `WIZARD_STEPS`), and `strategy-section-groups.test.ts`
 * holds both to that order and files each wizard step under the group of the
 * editor section it edits.
 */

import { en, type Translations } from "../../i18n/en";

export const STRATEGY_SECTION_GROUPS = [
    "foundation",
    "prices",
    "sellingRules",
    "calendar",
    "distribution",
] as const;

export type StrategySectionGroup = (typeof STRATEGY_SECTION_GROUPS)[number];

/** One stretch of consecutive items that share a group. */
export interface GroupRun<T> {
    /** Undefined for a stretch of items outside every group — the wizard's PMS, analysis and review steps. */
    group: StrategySectionGroup | undefined;
    items: T[];
}

/**
 * The items cut into runs of one group each, in their own order — what a list
 * draws a group heading over. Grouping by run rather than by key is what keeps
 * the order the caller already has, which is the one the surfaces agree on.
 */
export function groupRuns<T>(
    items: readonly T[],
    groupOf: (item: T) => StrategySectionGroup | undefined,
): GroupRun<T>[] {
    const runs: GroupRun<T>[] = [];
    for (const item of items) {
        const group = groupOf(item);
        const last = runs.at(-1);
        if (last && last.group === group) {
            last.items.push(item);
        } else {
            runs.push({ group, items: [item] });
        }
    }
    return runs;
}

/**
 * Each group's name in the interface language. Read here rather than threaded
 * through a label bag, because the names belong to the grouping and both
 * surfaces print the same five; defaulted group by group, since a partial `t`
 * is what the test harnesses hand these components.
 */
export function strategySectionGroupLabels(t: {
    strategySectionGroups?: Partial<Translations["strategySectionGroups"]>;
}): Record<StrategySectionGroup, string> {
    return { ...en.strategySectionGroups, ...(t.strategySectionGroups ?? {}) };
}
